/**
 * Product Hub — הזמנות. The order domain's vocabulary and its three pure decisions.
 *
 * An order is the module's only **historical** entity. Everything else here answers "what is true
 * now" (a price, a stock level, an effective fulfillment path); an order answers "what was true at
 * 12:43 on Tuesday", and it must keep answering that after the catalogue moves under it. Hence the
 * rule the whole domain is built around: **every displayed field is a snapshot on the row**, and
 * `variantId`/`productId` exist for traceability and for warehouse assignment — never for display.
 * `OrderItem` carries the product name the store sent even when nothing matched it.
 *
 * Three statuses live side by side and none of them derives the others (אפיון §8):
 *
 *  - `status` — the commercial state of the order (new → processing → completed / cancelled)
 *  - `paymentStatus` — what the money did, as reported by the store
 *  - `fulfillmentStatus` — where the goods are in the warehouse
 *
 * A paid order can still be unassigned, and a shipped order can be partially refunded. Folding any
 * pair into one column would make one of those unrepresentable.
 *
 * ## Why some names carry an `ORDER_` prefix and others do not
 *
 * The frontend imports everything from the root barrel, so every name here shares one namespace
 * with `payment.types.ts` (finance payments) and `subscriptionOrder.types.ts` (shipping orders
 * generated from subscriptions). `ORDER_PAYMENT_STATUSES` and `ORDER_FULFILLMENT_STATUSES` are
 * prefixed because the bare names are taken or would read as the other module's — the finance
 * `PAYMENT_STATUSES` are Hebrew-valued and mean "what our billing job did", which is a different
 * question from what a shop's checkout reported.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/orders`.
 */
import type { AuditFieldSpec } from './audit.types';
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Statuses                                                            */
/* ------------------------------------------------------------------ */

/** The commercial state (אפיון §8). English codes, Hebrew labels — ADR-017. */
export const ORDER_STATUSES = ['new', 'processing', 'completed', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'חדש',
  processing: 'בטיפול',
  completed: 'הושלם',
  cancelled: 'בוטל'
};

export const ORDER_STATUS_OPTIONS: PhOption<OrderStatus>[] = phOptions(ORDER_STATUSES, ORDER_STATUS_LABELS);

/**
 * What the money did — **as the store reported it**.
 *
 * We do not charge, refund or verify anything in this module: the shop's checkout did, and this
 * column is that report. `failed` and `pending` are therefore not our problem to fix in code; they
 * are a queue for a human, which is what the "בעיית תשלום" tile counts.
 */
export const ORDER_PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'partially_refunded', 'refunded'] as const;
export type OrderPaymentStatus = (typeof ORDER_PAYMENT_STATUSES)[number];

export const ORDER_PAYMENT_STATUS_LABELS: Record<OrderPaymentStatus, string> = {
  pending: 'ממתין',
  paid: 'שולם',
  failed: 'נכשל',
  partially_refunded: 'הוחזר חלקית',
  refunded: 'הוחזר'
};

export const ORDER_PAYMENT_STATUS_OPTIONS: PhOption<OrderPaymentStatus>[] = phOptions(
  ORDER_PAYMENT_STATUSES,
  ORDER_PAYMENT_STATUS_LABELS
);

/**
 * Where the goods are (אפיון §5, §8).
 *
 * `unassigned` is the entry state — an order with no warehouse behind it yet. `issue` is the one
 * value that is not a step forward: it means the operational path is blocked and the reason is in
 * `OrderIssues`. The picking values (`picking`/`picked`/`packed`) are written by hand from the order
 * for now; the warehouse screen that will advance them automatically is not part of this module.
 */
export const ORDER_FULFILLMENT_STATUSES = [
  'unassigned',
  'ready',
  'picking',
  'picked',
  'packed',
  'shipped',
  'ready_pickup',
  'completed',
  'issue'
] as const;
export type OrderFulfillmentStatus = (typeof ORDER_FULFILLMENT_STATUSES)[number];

export const ORDER_FULFILLMENT_STATUS_LABELS: Record<OrderFulfillmentStatus, string> = {
  unassigned: 'ממתין לשיבוץ',
  ready: 'מוכן לליקוט',
  picking: 'בליקוט',
  picked: 'לוקט',
  packed: 'נארז',
  shipped: 'נשלח',
  ready_pickup: 'מוכן לאיסוף',
  completed: 'הושלם',
  issue: 'בעיה'
};

export const ORDER_FULFILLMENT_STATUS_OPTIONS: PhOption<OrderFulfillmentStatus>[] = phOptions(
  ORDER_FULFILLMENT_STATUSES,
  ORDER_FULFILLMENT_STATUS_LABELS
);

/** Where the order came from: a shop, the office's "הזמנה חדשה" dialog, a signed quote, or a landing page. */
export const ORDER_SOURCES = ['store', 'manual', 'quote', 'landing'] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

/* ------------------------------------------------------------------ */
/* Issues — "דורש טיפול"                                               */
/* ------------------------------------------------------------------ */

/**
 * The exception types of אפיון §6, plus one the spec does not list, plus what a picker reports.
 *
 * `totals_mismatch` is ours: the store sends both the lines and the totals, and when they disagree
 * the order is still accepted (refusing it would lose a real sale over an arithmetic difference)
 * but somebody has to look. Silently trusting either side would mean the screen shows a number the
 * customer was never charged.
 *
 * The last five are the **physical** problems a warehouse worker finds while picking (מסך ליקוט
 * §36). They are in this list rather than in a table of their own precisely so that everything the
 * manager's screen already does — the alert at the top of the order, the "דורש טיפול" filter, the
 * KPI tile, `needsAttention` — covers them with no further work. A shortage is not among them: it
 * is `no_stock`/`partial_stock`, which intake already raises for the same fact.
 */
export const ORDER_ISSUE_TYPES = [
  'unknown_product',
  'sku_not_found',
  'no_stock',
  'partial_stock',
  'no_warehouse',
  'no_path',
  'unassigned',
  'payment',
  'sync',
  'shipping',
  'totals_mismatch',
  'not_found',
  'damaged',
  'wrong_item',
  'barcode_mismatch',
  'pick_other',
  'customer_ambiguous'
] as const;
export type OrderIssueType = (typeof ORDER_ISSUE_TYPES)[number];

export const ORDER_ISSUE_TYPE_LABELS: Record<OrderIssueType, string> = {
  unknown_product: 'מוצר לא מזוהה',
  sku_not_found: 'SKU לא קיים',
  no_stock: 'אין מלאי',
  partial_stock: 'מלאי חלקי',
  no_warehouse: 'אין מחסן מתאים',
  no_path: 'אין Fulfillment Path',
  unassigned: 'הזמנה לא שובצה',
  payment: 'תשלום לא תקין',
  sync: 'כשל סנכרון',
  shipping: 'בעיית שילוח',
  totals_mismatch: 'אי-התאמה בסכומים',
  // Worded from the manager's side: on their screen these arrive as a report from the floor, and
  // "המוצר לא נמצא" alone would read as a catalogue problem rather than a warehouse one.
  not_found: 'המוצר לא נמצא במחסן',
  damaged: 'מוצר פגום',
  wrong_item: 'מוצר שגוי',
  barcode_mismatch: 'ברקוד לא תואם',
  pick_other: 'בעיה אחרת בליקוט',
  // Raised at intake: the orderer's phone belongs to more than one customer card, so the order was
  // deliberately left unlinked rather than attached to a stranger's history. A person picks.
  customer_ambiguous: 'הטלפון מזוהה ליותר מלקוח אחד'
};

/**
 * The issues a **person in the warehouse** raised, as opposed to ones the system worked out.
 *
 * This is the list `reconcileOrder` must never own. Reconciliation resolves any issue in its own
 * list that it does not raise again, and stock is not moved by picking (see ADR-020 §4), so a
 * re-run would find the shelf exactly as full as before and quietly close a report that a person
 * made about a physical box. `damaged` is not a claim about stock levels; no amount of re-reading
 * them can re-decide it.
 */
export const PICKER_OWNED_ISSUE_TYPES: readonly OrderIssueType[] = [
  'not_found',
  'damaged',
  'wrong_item',
  'barcode_mismatch',
  'pick_other'
];

/**
 * The issue types reconciliation owns, and may therefore resolve on a re-run.
 *
 * `totals_mismatch` and `sync` are not in the list: they are statements about a payload we once
 * received, not about the current state of the stock, and a re-run has no way to re-decide them.
 * Letting reconciliation resolve them would quietly clear a money discrepancy because a warehouse
 * problem got fixed. `PICKER_OWNED_ISSUE_TYPES` are excluded for the stronger version of the same
 * reason, and `productHub.picking.spec.ts` asserts the two lists never overlap.
 *
 * It lives here rather than beside `persistIssues` in the backend so that assertion can be a unit
 * test with no database in it.
 */
export const RECONCILE_OWNED_ISSUE_TYPES: readonly OrderIssueType[] = [
  'unknown_product',
  'sku_not_found',
  'no_stock',
  'partial_stock',
  'no_warehouse',
  'no_path',
  'unassigned',
  'payment'
];

/**
 * Issues that mean the **system** could not resolve the order, so it is not warehouse work (§35).
 *
 * An order carrying one of these stays on the manager's screen and never becomes a pick task: there
 * is nothing a person holding a trolley can do about an unmatched SKU or a missing fulfillment
 * path. A physical issue is the opposite — it *stays* in the picker's list, in the "דורשות טיפול"
 * tile, which is exactly the split §36 draws.
 */
export const SYSTEM_BLOCKING_ISSUE_TYPES: readonly OrderIssueType[] = [
  'unknown_product',
  'sku_not_found',
  'no_warehouse',
  'no_path',
  'unassigned',
  'sync',
  'totals_mismatch'
];

/**
 * The blocking issues currently open on an order, in the order they should be read out.
 *
 * A list rather than a boolean because the refusal has to *name* them: "לא ניתן לסמן מוכן לליקוט —
 * SKU לא קיים" tells a manager what to fix, while "יש חריגה" sends them looking for it.
 */
export const blockingIssueTypesOf = (issueTypes: readonly OrderIssueType[]): OrderIssueType[] =>
  SYSTEM_BLOCKING_ISSUE_TYPES.filter(type => issueTypes.includes(type));

/**
 * Can this order be `ready`? The single definition of "מוכן לליקוט", read from both sides.
 *
 * **The invariant this file exists to keep: `ready` means pickable, and nothing else.** The picking
 * queue shows every `ready` order in the picker's warehouse, so an order that reaches `ready`
 * without a warehouse, or while the system still cannot resolve it, does not become a visible
 * problem — it becomes an invisible one, sitting on the manager's screen labelled "מוכן לליקוט"
 * while no warehouse queue anywhere contains it. That is exactly the state this predicate forbids.
 *
 * Physical issues (`no_stock`, `partial_stock`, the picker's own reports) are deliberately not
 * blockers here: those belong in the warehouse's "דורשות טיפול" tile, which is §36's whole point.
 */
export const canBeReadyForPicking = (input: {
  warehouseId: number | null;
  openIssueTypes: readonly OrderIssueType[];
}): boolean => input.warehouseId !== null && !blockingIssueTypesOf(input.openIssueTypes).length;

/** Why `ready` was refused, naming the blockers so the manager knows what to go and fix. */
export const readyForPickingRefusal = (input: {
  warehouseId: number | null;
  openIssueTypes: readonly OrderIssueType[];
}): string => {
  const blocking = blockingIssueTypesOf(input.openIssueTypes);
  const reason = blocking.length
    ? blocking.map(type => ORDER_ISSUE_TYPE_LABELS[type]).join(' · ')
    : 'ההזמנה טרם שובצה למחסן';

  return `לא ניתן לסמן "${ORDER_FULFILLMENT_STATUS_LABELS.ready}" — ${reason}`;
};

export const ORDER_ISSUE_TYPE_OPTIONS: PhOption<OrderIssueType>[] = phOptions(
  ORDER_ISSUE_TYPES,
  ORDER_ISSUE_TYPE_LABELS
);

/**
 * Which issues are a supply problem rather than a money problem.
 *
 * The split exists because the two have different owners: a warehouse person resolves the first
 * list, and whoever deals with the shop's payments resolves `payment`. `sync` and `shipping` sit
 * with supply — both stop the goods from moving.
 *
 * The picker's five are supply problems too, and that is the whole mechanism behind §29: a report
 * from the floor puts the order in the manager's "בעיית מלאי / אספקה" tile and flips
 * `needsAttention`, with nothing on the manager's screen having to know that picking exists.
 */
export const SUPPLY_ISSUE_TYPES: readonly OrderIssueType[] = [
  'unknown_product',
  'sku_not_found',
  'no_stock',
  'partial_stock',
  'no_warehouse',
  'no_path',
  'unassigned',
  'sync',
  'shipping',
  'not_found',
  'damaged',
  'wrong_item',
  'barcode_mismatch',
  'pick_other'
];

/* ------------------------------------------------------------------ */
/* KPI buckets (אפיון §3)                                              */
/* ------------------------------------------------------------------ */

export const ORDER_BUCKETS = ['all', 'pending', 'processing', 'supply', 'payment'] as const;
export type OrderBucket = (typeof ORDER_BUCKETS)[number];

export const ORDER_BUCKET_LABELS: Record<OrderBucket, string> = {
  all: 'סה"כ הזמנות',
  pending: 'ממתינות לטיפול',
  processing: 'בטיפול',
  supply: 'בעיית מלאי / אספקה',
  payment: 'בעיית תשלום'
};

export const ORDER_BUCKET_HINTS: Record<OrderBucket, string> = {
  all: 'בכל החנויות',
  pending: 'טרם התחיל טיפול תפעולי',
  processing: 'בתהליך Fulfillment',
  supply: 'דורשות טיפול',
  payment: 'תשלום שנכשל או ממתין'
};

/** The minimum an order has to expose for the bucket predicates to read it. */
export interface OrderBucketInput {
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  /** Open issues only — a resolved issue must not keep an order in a queue. */
  issueTypes: readonly OrderIssueType[];
}

/**
 * The four bucket definitions, as predicates.
 *
 * **These are the definition; the SQL in `orders.buckets.ts` is a translation of it.** The two are
 * necessarily separate expressions — a `COUNT(*) FILTER (…)` cannot call a TypeScript function —
 * and the pair is deliberate: the predicates carry the spec test, and `needsAttention` on the row
 * is written from `orderNeedsAttention` below, so the tile, the filter and the row icon cannot end
 * up with three different opinions. Change one and the other must be changed in the same commit.
 */
export const isPendingOrder = (order: OrderBucketInput): boolean =>
  order.status === 'new' || (order.fulfillmentStatus === 'unassigned' && order.status !== 'cancelled');

export const isProcessingOrder = (order: OrderBucketInput): boolean => order.status === 'processing';

export const hasSupplyProblem = (order: OrderBucketInput): boolean =>
  order.fulfillmentStatus === 'issue' || order.issueTypes.some(type => SUPPLY_ISSUE_TYPES.includes(type));

export const hasPaymentProblem = (order: OrderBucketInput): boolean =>
  order.paymentStatus === 'failed' ||
  order.paymentStatus === 'pending' ||
  order.issueTypes.includes('payment') ||
  order.issueTypes.includes('totals_mismatch');

/**
 * The flag stored on the row, so filtering and the row icon cost nothing.
 *
 * A cancelled order is never "requiring attention": it is closed, and leaving it in the queue means
 * the count never reaches zero.
 */
export const orderNeedsAttention = (order: OrderBucketInput): boolean =>
  order.status !== 'cancelled' && (hasSupplyProblem(order) || hasPaymentProblem(order));

/* ------------------------------------------------------------------ */
/* Transitions                                                         */
/* ------------------------------------------------------------------ */

/**
 * Which commercial statuses may follow which (אפיון §22 keeps the action list short).
 *
 * `completed` and `cancelled` are terminal. Re-opening a completed order would let somebody change
 * a shipment that has already left, and un-cancelling is a decision with money behind it — both are
 * deliberately not offered rather than guarded with a warning.
 */
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  new: ['processing', 'completed', 'cancelled'],
  processing: ['completed', 'cancelled'],
  completed: [],
  cancelled: []
};

export const canTransitionOrderStatus = (from: OrderStatus, to: OrderStatus): boolean =>
  from === to || ORDER_STATUS_TRANSITIONS[from].includes(to);

export const isOrderCancellable = (status: OrderStatus): boolean =>
  ORDER_STATUS_TRANSITIONS[status].includes('cancelled');

/** Refusing to cancel: only ever reached from a terminal status, so the reason is that. */
export const ORDER_CANCEL_REFUSED = 'לא ניתן לבטל הזמנה שהושלמה או בוטלה';

/**
 * Why a status change was refused, in the words of the change itself.
 *
 * A single constant used to say "הזמנה שהושלמה או בוטלה" for every refusal, including
 * `בטיפול → חדש` — where it named a reason that had nothing to do with the case and left the reader
 * looking for a completed order that was not there.
 */
export const orderTransitionRefusal = (from: OrderStatus, to: OrderStatus): string => {
  const move = `לא ניתן לשנות סטטוס מ"${ORDER_STATUS_LABELS[from]}" ל"${ORDER_STATUS_LABELS[to]}"`;
  return ORDER_STATUS_TRANSITIONS[from].length ? `${move} — הזמנה אינה חוזרת אחורה` : `${move} — הסטטוס סופי`;
};

/**
 * The fulfillment status a freshly reconciled order lands on.
 *
 * Not a free choice: it is the one place where "we found a warehouse" and "we did not" turn into a
 * value the warehouse acts on, and putting it here keeps the intake service and the "שבץ מחדש"
 * action from drifting apart.
 */
export const initialOrderFulfillmentStatus = (input: {
  warehouseId: number | null;
  hasSupplyIssue: boolean;
  /**
   * Every issue open on the order right now — **not** only the ones reconciliation just raised.
   *
   * Required rather than optional, because leaving it out is the bug it was added for: intake
   * raises `totals_mismatch` outside `assembleIssues`, so a caller reasoning from the raised list
   * alone concluded "no supply problem" and landed the order on `ready` while a blocking issue kept
   * it out of every picking queue. Nothing then resolved it — `totals_mismatch` is not
   * reconciliation-owned — so the order stayed "מוכן לליקוט" and unpickable for good.
   */
  openIssueTypes: readonly OrderIssueType[];
}): OrderFulfillmentStatus => {
  if (input.hasSupplyIssue || blockingIssueTypesOf(input.openIssueTypes).length) return 'issue';
  return input.warehouseId ? 'ready' : 'unassigned';
};

/* ------------------------------------------------------------------ */
/* Row shapes                                                          */
/* ------------------------------------------------------------------ */

/** One historical line. Every field but the two ids is a snapshot (אפיון §16). */
export interface OrderItem {
  id: number;
  orderId: number;
  /** Null when the store sent a SKU we could not match — `productName` is what keeps it readable. */
  variantId: number | null;
  productId: number | null;
  /** The name **as the store sent it**, kept even when a variant was matched. */
  productName: string;
  variantLabel: string | null;
  sku: string | null;
  imageUrl: string | null;
  quantity: number;
  unitPrice: number;
  lineDiscount: number;
  lineTotal: number;
  /** False = "מוצר לא מזוהה · SKU לא נמצא", which is what the drawer and the row icon show. */
  matched: boolean;
  /**
   * How many units the warehouse actually collected (מסך ליקוט §33).
   *
   * The one column on a line that is not a snapshot of the sale, and it is not one for the same
   * reason `fulfillmentStatus` is not: it records what happened in the building, not what was
   * bought. `quantity` beside it never moves.
   */
  pickedQuantity: number;
}

export interface OrderIssue {
  id: number;
  orderId: number;
  type: OrderIssueType;
  /** Hebrew, written by the server: "חסרות 2 יחידות עבור SKU SDR-5001-EH". */
  detail: string;
  resolvedAt: string | null;
}

export interface OrderNote {
  id: number;
  orderId: number;
  userId: number | null;
  authorName: string | null;
  body: string;
  createdAt: string;
}

/** A postal address, snapshotted. Both the shipping and the billing address use this shape. */
export interface OrderAddress {
  recipientName: string | null;
  street: string | null;
  houseNumber: string | null;
  apartment: string | null;
  city: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
}

/** One row of the orders table. Names are resolved by the server so a cell never joins. */
export interface Order {
  id: number;
  storeId: number;
  storeName: string;
  /** The store's own opaque id for this order — the idempotency half of the intake key. */
  externalOrderId: string;
  /** What the customer quotes on the phone, and what the first column shows. */
  externalNumber: string;
  /** ISO. Displayed as date + time (אפיון §8). */
  placedAt: string;
  currency: string;

  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  needsAttention: boolean;

  /**
   * The person who placed the order — four separate facts, all of them required at intake.
   *
   * `customerName` is **not** a column. It is composed by the serializer from the two names, so
   * the grid, the export and the picking screen keep reading one field while a template can greet
   * somebody by their first name alone. Composing it here rather than storing it means the two can
   * never disagree.
   */
  customerFirstName: string;
  customerLastName: string;
  /** Derived, never stored — see above. */
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  /**
   * The customer card this order was matched to, by normalised phone.
   *
   * Null is not "no customer": it means the phone matched **more than one** card, and guessing
   * would attach somebody's order to a stranger's history. That case opens an `OrderIssue`
   * instead. Mailing does not depend on this — it addresses the snapshot above.
   */
  customerId: number | null;

  warehouseId: number | null;
  warehouseName: string | null;
  shippingMethodId: number | null;
  /** Snapshot: the method may be renamed or archived after the order was placed. */
  shippingMethodName: string | null;
  shippingMethodType: string | null;
  shippingProviderId: number | null;
  shippingProviderName: string | null;
  trackingNumber: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;

  unitsCount: number;
  linesCount: number;

  itemsSubtotal: number;
  discountTotal: number;
  shippingTotal: number;
  taxTotal: number;
  refundTotal: number;
  grandTotal: number;

  /** Open issues, for the row's "דורש טיפול" indicator and its tooltip. */
  issues: OrderIssue[];

  version: number;
  createdAt: string;
}

/** The order page. Adds everything a row does not need to carry. */
export interface OrderDetail extends Order {
  shipTo: OrderAddress;
  billingSameAsShipping: boolean;
  /** Null when it is the same as the shipping address (אפיון §15). */
  billTo: OrderAddress | null;
  /** Read-only, arrived with the order (אפיון §20). */
  customerNote: string | null;
  pickupPointId: number | null;
  pickupPointName: string | null;
  pickupPointAddress: string | null;
  readyForPickupAt: string | null;
  pickedUpAt: string | null;
  source: OrderSource;
  items: OrderItem[];
  notes: OrderNote[];
}

/** The five tiles of אפיון §3. Counted over every order, not over the current filter. */
export type OrderSummary = Record<OrderBucket, number>;

/** Quick date ranges (אפיון §5). `custom` means `startDate`/`endDate` are used instead. */
export const ORDER_DATE_PRESETS = ['today', 'yesterday', 'last7', 'last30', 'month', 'custom'] as const;
export type OrderDatePreset = (typeof ORDER_DATE_PRESETS)[number];

export const ORDER_DATE_PRESET_LABELS: Record<OrderDatePreset, string> = {
  today: 'היום',
  yesterday: 'אתמול',
  last7: '7 ימים אחרונים',
  last30: '30 ימים אחרונים',
  month: 'החודש',
  custom: 'טווח מותאם אישית'
};

export const ORDER_DATE_PRESET_OPTIONS: PhOption<OrderDatePreset>[] = phOptions(
  ORDER_DATE_PRESETS,
  ORDER_DATE_PRESET_LABELS
);

/**
 * List params, all as strings — they come from the URL query string and go back to it.
 *
 * The multi-value ones are comma-separated, which is what `GridData`'s `multiSelect` writes and
 * what the other Product Hub list endpoints already read.
 */
export interface OrderListParams {
  page?: string;
  rowsPerPage?: string;
  sortField?: string;
  sortOrder?: string;
  searchTerm?: string;
  storeId?: string;
  status?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  /** Warehouse ids, plus the literal `none` for "ללא מחסן משויך". */
  warehouseId?: string;
  shippingMethodId?: string;
  issueType?: string;
  datePreset?: string;
  startDate?: string;
  endDate?: string;
  bucket?: string;
}

/* ------------------------------------------------------------------ */
/* Manual creation — an order typed in on the orders screen            */
/* ------------------------------------------------------------------ */

/**
 * One line of an order somebody is typing in.
 *
 * `variantId` and not a name: a manual order is placed against the **catalogue**, so the product
 * name, the SKU, the variation label and the image are all resolved on the server from the variant
 * and snapshotted from there. Letting the browser send them would make the one field that must
 * never drift — what the customer bought — a value a client could get wrong.
 *
 * `unitPrice` *is* sent, because it is the one line field an operator legitimately overrides: the
 * price book fills it in, and a phone order sometimes carries an agreed price that is not the
 * listed one.
 */
/**
 * Who ordered — the same four required fields on both intake paths (a shop posting to
 * `/api/store-orders/v1` and the office typing a phone order).
 *
 * All four are mandatory, and that is a deliberate tightening of the external contract: the
 * orderer has to be able to receive a receipt, so an address and a number are part of what makes
 * the order complete rather than optional extras. Two consequences worth stating where the type
 * lives — a shop that used to post a nameless-but-emailless order now gets a 400, and the office's
 * "הזמנה חדשה" dialog asks for all four too, because it goes through this same shape.
 *
 * There is no single `name` field and no splitting of one. "ישראל בן דוד כהן" cannot be split
 * correctly by any rule, and a message that greets somebody by the wrong name is worse than one
 * that greets them by their full name.
 */
export interface OrderCustomerInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/**
 * The display name, composed rather than stored.
 *
 * One place, because it is read by the orders grid, the export, the picking screen, the customer
 * card that intake opens, and every template that uses `{שם מלא}` — and a second implementation
 * would eventually disagree with this one about the space.
 */
export const orderCustomerName = (firstName: string | null, lastName: string | null): string =>
  [firstName, lastName]
    .map(part => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');

export interface ManualOrderLine {
  variantId: number;
  quantity: number;
  unitPrice: number;
}

/**
 * What `POST /pvt/orders` accepts — the "הזמנה חדשה" dialog on the orders screen.
 *
 * The office placing an order on somebody's behalf (a phone order), as opposed to a shop posting
 * one to `/api/store-orders/v1`. It goes through **the same pipeline**: the server builds an
 * `OrderIntakePayload` from this and hands it to the intake service, so SKU matching, exception
 * detection, warehouse assignment and the audit entry are one code path rather than two that have
 * to be kept agreeing.
 *
 * There is no `paymentStatus` and no discount: §22 keeps charging, refunds and line editing out of
 * this module, and an order the office typed starts `pending` like any other until somebody settles
 * it. There is no delivery charge either — `shippingTotal` stays 0 — so the total is the lines.
 */
export interface ManualOrderBody {
  storeId: number;
  /** Chosen by the operator. Honoured by the resolver rather than overruled — see `resolveOrderPath`. */
  shippingMethodId: number;
  customer: OrderCustomerInput;
  shipTo: Partial<OrderAddress>;
  customerNote?: string | null;
  items: ManualOrderLine[];
}

/** What `PATCH /pvt/orders/:id` accepts. `version` is the optimistic lock, not an editable field. */
export interface OrderUpdateBody {
  version: number;
  status?: OrderStatus;
  fulfillmentStatus?: OrderFulfillmentStatus;
  /** `null` clears the assignment, which is a legitimate act — see `orderNeedsAttention`. */
  warehouseId?: number | null;
  trackingNumber?: string | null;
}

/* ------------------------------------------------------------------ */
/* Intake payload                                                      */
/* ------------------------------------------------------------------ */

/**
 * What a shop posts to `/api/store-orders/v1/orders`.
 *
 * camelCase throughout, like the outbound feed — one convention for the whole external surface so
 * an integrator has no per-field rule to remember. There is **no `storeId`**: the key identifies
 * the store, exactly as in the catalogue feed, which is what makes "shop A files an order against
 * shop B" unrepresentable rather than merely forbidden.
 */
export interface OrderIntakeItem {
  sku?: string | null;
  externalVariantId?: string | null;
  name: string;
  variantLabel?: string | null;
  imageUrl?: string | null;
  quantity: number;
  unitPrice: number;
  discount?: number;
  lineTotal?: number;
}

export interface OrderIntakePayload {
  externalOrderId: string;
  orderNumber: string;
  placedAt: string;
  currency?: string;
  status?: OrderStatus;
  paymentStatus?: OrderPaymentStatus;
  customer: OrderCustomerInput;
  shipTo?: Partial<OrderAddress> | null;
  billTo?: Partial<OrderAddress> | null;
  shippingMethodCode?: string | null;
  shippingMethodName?: string | null;
  customerNote?: string | null;
  items: OrderIntakeItem[];
  totals: {
    itemsSubtotal: number;
    discountTotal?: number;
    shippingTotal?: number;
    taxTotal?: number;
    grandTotal: number;
  };
}

/**
 * Money the store reported, checked but never recomputed.
 *
 * We do not re-derive tax and we do not strip it out: `taxTotal` is a column of its own, so the
 * agora that a strip-then-re-add loses on some amounts is never lost here. All this does is notice
 * that the parts do not add up to the whole, to a tolerance of one agora for the shop's own
 * rounding, and let the intake record it as an issue.
 */
export const totalsAgree = (totals: OrderIntakePayload['totals']): boolean => {
  const parts =
    totals.itemsSubtotal - (totals.discountTotal ?? 0) + (totals.shippingTotal ?? 0) + (totals.taxTotal ?? 0);
  return Math.abs(parts - totals.grandTotal) <= 0.01;
};

/* ------------------------------------------------------------------ */
/* Audit                                                               */
/* ------------------------------------------------------------------ */

/**
 * Exactly the columns a write may touch. Nothing else on the order is mutable after intake.
 *
 * A named type rather than `Partial<Order>` because the distinction is the point: `Order` also
 * carries resolved names and totals, and a patch that reached those would be rewriting history.
 */
export interface OrderWritableFields extends Record<string, unknown> {
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  warehouseId: number | null;
  trackingNumber: string | null;
  // `Date | string` because both sides of the diff pass through here: the row as Sequelize loaded it
  // (a `Date`) and the patch as it arrived (an ISO string). The audit differ renders both the same
  // way, and forcing one of them would just move the cast to the caller.
  shippedAt: Date | string | null;
  deliveredAt: Date | string | null;
  readyForPickupAt: Date | string | null;
  pickedUpAt: Date | string | null;
  needsAttention: boolean;
}

/**
 * The columns a user can change, for `auditedUpdate`.
 *
 * **Every writable column must appear here.** `auditedUpdate` returns early when the diff is empty
 * and then writes nothing at all, so a column missing from this list is not merely unlogged — it is
 * unsaveable, and the request still answers 200. `needsAttention` is in the list for that reason
 * alone: it is derived, never typed by a person, but it rides along on the same patch.
 *
 * The `format` functions are what keep ids out of the history: "מחסן מרכזי — לוד" rather than "3".
 * Warehouse names are not in this file, so the caller passes a resolver — hence the factory.
 */
export const orderAuditSpec = (
  warehouseName: (id: number | null) => string | null
): AuditFieldSpec<OrderWritableFields>[] => [
  { key: 'status', label: 'סטטוס הזמנה', format: value => ORDER_STATUS_LABELS[value as OrderStatus] ?? null },
  {
    key: 'paymentStatus',
    label: 'סטטוס תשלום',
    format: value => ORDER_PAYMENT_STATUS_LABELS[value as OrderPaymentStatus] ?? null
  },
  {
    key: 'fulfillmentStatus',
    label: 'סטטוס אספקה',
    format: value => ORDER_FULFILLMENT_STATUS_LABELS[value as OrderFulfillmentStatus] ?? null
  },
  {
    key: 'warehouseId',
    label: 'מחסן מטפל',
    format: value => warehouseName((value as number | null) ?? null) ?? null
  },
  { key: 'trackingNumber', label: 'מספר מעקב' },
  { key: 'shippedAt', label: 'תאריך יציאה' },
  { key: 'deliveredAt', label: 'תאריך מסירה' },
  { key: 'readyForPickupAt', label: 'מוכן לאיסוף בתאריך' },
  { key: 'pickedUpAt', label: 'נאסף בתאריך' },
  { key: 'needsAttention', label: 'דורש טיפול' }
];
